/**
 * The multiplayer screens' pure model: what a connection LOOKS like to the
 * menu, and the text-field editor the address and code prompts run on.
 *
 * Same split as ./training-model against ./training: everything here is data
 * and pure functions, so the lobby's sorting, its labels, its ping colours and
 * every keystroke of the text prompt are testable in Node with no socket and no
 * DOM. `src/ui/net-runtime.ts` owns the live `NetClient` and writes a `NetView`
 * into the menu; `src/ui/menu-model.ts` reads one and decides nothing about the
 * network itself.
 *
 * ===========================================================================
 * THE MENU NEVER GUESSES THE SERVER'S ANSWER
 * ===========================================================================
 *
 * `NetView` is a mirror, not a model: `ready`, `team`, the roster and the code
 * are whatever the last `RoomState` said, and pressing Ready does not flip the
 * row — it sends a message, and the row moves when the server agrees. That is
 * the same rule the rest of this front end is held to ("a control that cannot
 * work must not look like it worked"), applied to the case where the control
 * works but somebody else decides the outcome. A lobby that shows you as ready
 * when the server never received it is how a room sits waiting forever with
 * everybody's screen saying it should have started.
 */

import type { MultiplayerMode } from '../net/protocol';
import type { NetFailure, NetPhase } from '../net/client';
import {
  MAX_DISPLAY_NAME,
  sanitizeDisplayName,
  sanitizeRoomCode,
  sanitizeServerAddress,
} from '../game/settings';
import { normalizeServerUrl } from '../net/ws-transport';

// ---------------------------------------------------------------------------
// The view
// ---------------------------------------------------------------------------

/** One line of the lobby list and of the in-match scoreboard. */
export interface NetPlayerRow {
  playerId: number;
  name: string;
  team: 0 | 1;
  /** Index into `World.cars`, or null for a spectator. */
  carIndex: number | null;
  car: string;
  ready: boolean;
  isBot: boolean;
  /** Round trip in ms, as the server measured it. */
  ping: number;
  /** True for the row belonging to this client. */
  isLocal: boolean;
}

export interface NetQueueView {
  waiting: number;
  needed: number;
  waitedMs: number;
}

/**
 * Everything the multiplayer screens draw. Written by the runtime, read by the
 * model and the view; nothing mutates it in place.
 */
export interface NetView {
  phase: NetPhase;
  /** Why it ended, when it has. */
  failure: NetFailure | null;
  /** One sentence, always present, describing exactly the current phase. */
  message: string;
  mode: MultiplayerMode;
  /** The join code, or null outside a room. */
  code: string | null;
  teamSize: number;
  /** False for `freeplay`, where the team column is meaningless. */
  teams: boolean;
  inProgress: boolean;
  players: readonly NetPlayerRow[];
  /** This client's row, or null before the roster arrives. */
  local: NetPlayerRow | null;
  /** Round trip this client is seeing, ms. */
  ping: number;
  /** True when the link is bad enough to warn about. */
  degraded: boolean;
  /** Reconnect attempt in flight, 1-based. Zero when none. */
  retry: number;
  retryLimit: number;
  /** True when a seat is still being held and a reconnect could take it. */
  canReconnect: boolean;
  /** False when there is no clipboard to copy the code to. */
  canCopy: boolean;
  queue: NetQueueView | null;
}

export const idleNetView = (): NetView => ({
  phase: 'idle',
  failure: null,
  message: 'Not connected.',
  mode: 'duel',
  code: null,
  teamSize: 1,
  teams: true,
  inProgress: false,
  players: [],
  local: null,
  ping: 0,
  degraded: false,
  retry: 0,
  retryLimit: 0,
  canReconnect: false,
  canCopy: false,
  queue: null,
});

/** True while a connection is being made or is alive. */
export const netBusy = (view: NetView): boolean =>
  view.phase === 'connecting' || view.phase === 'handshake' || view.phase === 'queued';

export const netLive = (view: NetView): boolean =>
  view.phase === 'lobby' || view.phase === 'playing';

// ---------------------------------------------------------------------------
// Rosters
// ---------------------------------------------------------------------------

/**
 * The roster in display order: blue first, then orange, then the stands, and
 * within a team by seat.
 *
 * Sorted by SEAT rather than by name or by join order, because the seat is what
 * the player sees on the pitch — the second blue car is always the second blue
 * row. A list that reorders itself when somebody renames or when a ping updates
 * is one you cannot read at a glance mid-match, which is the only time the
 * scoreboard is open.
 */
export const sortedRoster = (players: readonly NetPlayerRow[]): NetPlayerRow[] =>
  [...players].sort((a, b) => {
    // Spectators last, whatever team the server nominally gave them.
    const aSpec = a.carIndex === null ? 1 : 0;
    const bSpec = b.carIndex === null ? 1 : 0;
    if (aSpec !== bSpec) return aSpec - bSpec;
    if (a.team !== b.team) return a.team - b.team;
    const aSeat = a.carIndex ?? 0;
    const bSeat = b.carIndex ?? 0;
    if (aSeat !== bSeat) return aSeat - bSeat;
    return a.playerId - b.playerId;
  });

/** Humans on a team, bots excluded — what "3 of 3" on the lobby means. */
export const humansOn = (players: readonly NetPlayerRow[], team: 0 | 1): number =>
  players.filter(p => !p.isBot && p.carIndex !== null && p.team === team).length;

/** True when every connected human has readied and there is at least one. */
export const everyoneReady = (players: readonly NetPlayerRow[]): boolean => {
  const humans = players.filter(p => !p.isBot);
  return humans.length > 0 && humans.every(p => p.ready);
};

/**
 * Ping as a label and a band.
 *
 * Three bands rather than a gradient, because the only decision a player makes
 * from this number is "is it me or is it them", and three colours answer that
 * where a continuous ramp does not. The thresholds are where the game actually
 * changes: under 60 ms the prediction hides everything, past 120 ms the
 * correction on a 50/50 is visible, and past 200 ms it is the game.
 */
export type PingBand = 'good' | 'fair' | 'poor';

export const pingBand = (ms: number): PingBand => (ms < 60 ? 'good' : ms < 120 ? 'fair' : 'poor');

export const pingLabel = (ms: number): string =>
  !Number.isFinite(ms) || ms <= 0 ? '—' : `${Math.round(ms)} ms`;

// ---------------------------------------------------------------------------
// Status lines
// ---------------------------------------------------------------------------

const modeLabel = (view: NetView): string => `${view.teamSize}v${view.teamSize}`;

/**
 * The line under the title, for whichever multiplayer screen is up.
 *
 * Every phase has one and none of them is empty: a connecting screen with no
 * text is indistinguishable from a frozen one, and "the game hung" is the
 * conclusion a player reaches in about two seconds.
 */
export const netStatusLine = (view: NetView): string => {
  switch (view.phase) {
    case 'idle':
      return 'Not connected.';
    case 'connecting':
      return view.retry > 0
        ? `Reconnecting — attempt ${view.retry} of ${view.retryLimit}.`
        : 'Opening a connection…';
    case 'handshake':
      return 'Connected. Finding a match…';
    case 'queued': {
      const queue = view.queue;
      if (queue === null) return 'Waiting for a match…';
      const seconds = Math.floor(queue.waitedMs / 1000);
      return `In the ${modeLabel(view)} queue — ${queue.waiting} of ${queue.needed} waiting, ${seconds}s.`;
    }
    case 'lobby':
      return view.code === null
        ? 'In a lobby.'
        : `Room ${view.code} · ${modeLabel(view)} · waiting for everyone to ready up.`;
    case 'playing':
      return `Room ${view.code ?? '—'} · ${modeLabel(view)} · live.`;
    case 'closed':
      return view.message;
  }
};

// ---------------------------------------------------------------------------
// Text fields
// ---------------------------------------------------------------------------

export type TextFieldId =
  | 'serverAddress'
  | 'roomCode'
  | 'displayName'
  // --- account ---
  | 'accountEmail'
  | 'accountPassword'
  | 'accountName';

export interface TextFieldSpec {
  id: TextFieldId;
  label: string;
  /** Shown under the prompt. Says what a valid value looks like. */
  hint: string;
  maxLength: number;
  /** Fold to upper case as it is typed. */
  upper: boolean;
  /** Render as dots — a password. The buffer itself is never shown or stored. */
  mask?: boolean;
}

export const TEXT_FIELDS: Readonly<Record<TextFieldId, TextFieldSpec>> = {
  serverAddress: {
    id: 'serverAddress',
    label: 'Server Address',
    hint: 'host:port, or a full ws:// or wss:// URL. /play is added when the path is left off.',
    maxLength: 200,
    upper: false,
  },
  roomCode: {
    id: 'roomCode',
    label: 'Join Code',
    hint: 'Four to eight letters and digits, from whoever is hosting.',
    maxLength: 8,
    upper: true,
  },
  displayName: {
    id: 'displayName',
    label: 'Display Name',
    hint: `Up to ${MAX_DISPLAY_NAME} characters. Everyone in the room sees it.`,
    maxLength: MAX_DISPLAY_NAME,
    upper: false,
  },
  accountEmail: {
    id: 'accountEmail',
    label: 'Email',
    hint: 'The address the verification mail goes to.',
    maxLength: 254,
    upper: false,
  },
  accountPassword: {
    id: 'accountPassword',
    label: 'Password',
    hint: 'At least 8 characters. Held in memory only, never stored.',
    maxLength: 128,
    upper: false,
    mask: true,
  },
  accountName: {
    id: 'accountName',
    label: 'Player Name',
    hint: `3 to ${MAX_DISPLAY_NAME} characters — letters, digits, spaces, _ . - — unique per account.`,
    maxLength: MAX_DISPLAY_NAME,
    upper: false,
  },
};

/** What a text buffer shows on screen: itself, or one dot per character. */
export const displayTextValue = (value: string, spec: TextFieldSpec): string =>
  spec.mask === true ? '•'.repeat(value.length) : value;

export type TextEditOutcome = 'edit' | 'commit' | 'cancel' | 'ignored';

export interface TextEditResult {
  buffer: string;
  outcome: TextEditOutcome;
}

/**
 * One keystroke against a text buffer.
 *
 * Takes `code` AND `key` because they answer different questions: `code` is the
 * physical key and is what Escape, Enter and Backspace must be matched on so a
 * non-US layout still cancels; `key` is the character produced and is the only
 * thing that can be appended. Matching Escape on `key` breaks nothing and
 * matching a character on `code` gives a Dvorak typist somebody else's letters.
 *
 * Modified keystrokes are IGNORED rather than inserted: Ctrl+V must not type a
 * `v` into the address field, which is exactly what a naive `key.length === 1`
 * check does and is the most annoying possible bug in a field people paste into.
 */
export const editText = (
  buffer: string,
  event: { code: string; key: string; ctrlKey?: boolean; metaKey?: boolean; altKey?: boolean },
  spec: TextFieldSpec
): TextEditResult => {
  if (event.code === 'Escape') return { buffer, outcome: 'cancel' };
  if (event.code === 'Enter' || event.code === 'NumpadEnter') {
    return { buffer, outcome: 'commit' };
  }
  if (event.code === 'Backspace') {
    return { buffer: buffer.slice(0, -1), outcome: buffer.length > 0 ? 'edit' : 'ignored' };
  }
  // The prompt opens holding the CURRENT value, because an address is far more
  // often tweaked than retyped. That makes a one-press way to empty it
  // necessary rather than a nicety: without it, replacing a 40-character URL is
  // forty presses of Backspace. `Delete` is the same key the bind prompt clears
  // a slot with, so there is one thing to remember rather than two.
  if (event.code === 'Delete') {
    return { buffer: '', outcome: buffer.length > 0 ? 'edit' : 'ignored' };
  }
  if (event.ctrlKey === true || event.metaKey === true || event.altKey === true) {
    return { buffer, outcome: 'ignored' };
  }
  // `key.length === 1` is the only reliable "this produced a character" test:
  // every named key ('Shift', 'ArrowLeft', 'Dead') is longer, and every printable
  // one — including accented and non-Latin characters — is exactly one unit.
  if (event.key.length !== 1) return { buffer, outcome: 'ignored' };
  if (buffer.length >= spec.maxLength) return { buffer, outcome: 'ignored' };
  const char = spec.upper ? event.key.toUpperCase() : event.key;
  return { buffer: buffer + char, outcome: 'edit' };
};

/** Pasted text, truncated and folded the same way typing would be. */
export const pasteText = (buffer: string, text: string, spec: TextFieldSpec): string => {
  const folded = spec.upper ? text.toUpperCase() : text;
  // Newlines and tabs come with anything copied out of a chat window and would
  // sit in the buffer invisibly, failing validation for no visible reason.
  const keepSpaces = spec.id === 'displayName' || spec.id === 'accountName';
  const flat = folded.replace(/\s+/g, keepSpaces ? ' ' : '');
  return (buffer + flat).slice(0, spec.maxLength);
};

export interface TextCommit {
  /** The value to store, or null when it is not usable. */
  value: string | null;
  /** Why not, when it is null. Shown as a toast; the prompt stays open. */
  error: string;
}

/**
 * Validate a committed buffer, through the SAME sanitizers the stored settings
 * use.
 *
 * A field that accepts what storage will later reject is a field that silently
 * reverts on the next reload, and one that accepts an address the transport
 * will refuse is a field whose failure appears two screens later with no
 * connection to what was typed.
 */
export const commitText = (field: TextFieldId, buffer: string): TextCommit => {
  switch (field) {
    case 'serverAddress': {
      const url = normalizeServerUrl(buffer);
      if (url === null) {
        return { value: null, error: 'Not an address. Try host:port, or a full ws:// URL.' };
      }
      return { value: sanitizeServerAddress(url), error: '' };
    }
    case 'roomCode': {
      const code = sanitizeRoomCode(buffer);
      if (code === '') {
        return { value: null, error: 'A join code is four to eight letters and digits.' };
      }
      return { value: code, error: '' };
    }
    case 'displayName':
      // Total: any input at all yields a usable name, so this one cannot fail.
      return { value: sanitizeDisplayName(buffer), error: '' };

    // The account fields are validated FOR SHAPE only — the server is the
    // authority and revalidates everything. These checks exist so an obvious
    // mistake is caught in the prompt rather than a round trip later.
    case 'accountEmail': {
      const email = buffer.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        return { value: null, error: 'That does not look like an email address.' };
      }
      return { value: email, error: '' };
    }
    case 'accountPassword': {
      if (buffer.length < 8) {
        return { value: null, error: 'A password is at least 8 characters.' };
      }
      return { value: buffer, error: '' };
    }
    case 'accountName': {
      const name = buffer.trim();
      if (name.length < 3) {
        return { value: null, error: 'A name is at least 3 characters.' };
      }
      if (!/^[A-Za-z0-9 _.-]+$/.test(name)) {
        return { value: null, error: 'Letters, digits, spaces, _ . - only.' };
      }
      return { value: name, error: '' };
    }
  }
};
